import { useEffect, useState } from "react";
import { BellRinging, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// A visitor who likes the board but has nothing near them yet leaves an address instead of
// leaving for good. Stored for the operator — nothing is emailed out automatically.
export const NotifyMe = ({ source = "farmers-market", testid = "notify", blurb = "" }) => {
  const [options, setOptions] = useState([]);
  const [email, setEmail] = useState("");
  const [where, setWhere] = useState("");
  const [want, setWant] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/public-site/notify/interests")
      .then(({ data }) => setOptions(data.interests || []))
      .catch(() => setOptions([]));
  }, []);

  const toggle = (i) =>
    setWant(want.includes(i) ? want.filter((x) => x !== i) : [...want, i]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/public-site/notify", {
        email, where, interests: want, source });
      setDone(true);
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section className="mt-10 border border-[#0B5C2E] bg-[#F3FAF5] p-5" data-testid={`${testid}-done`}>
        <p className="flex items-start gap-2 text-sm leading-relaxed text-slate-700">
          <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-[#0B5C2E]" />
          You are on the list. We will let you know as stalls open near {where || "you"} —
          and nothing else.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-10 border border-border bg-white p-5" data-testid={`${testid}-box`}>
      <p className="overline text-[#0B5C2E]">
        <BellRinging size={13} className="mr-1 inline align-[-2px]" /> Nothing near you yet?
      </p>
      <h2 className="font-display mt-1 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
        Tell us where you are and we will let you know
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">
        {blurb || "New stalls, fields, dairies, boats and butchers are joining all the time. Leave your address and the town you are in, and we will tell you when there is something worth the walk."} No newsletter, no selling it on.
      </p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <Input data-testid={`${testid}-email`} type="email" placeholder="you@example.com"
          value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input data-testid={`${testid}-where`} placeholder="Town or postcode"
          value={where} onChange={(e) => setWhere(e.target.value)} />
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {options.map((i) => (
          <button key={i} type="button" data-testid={`${testid}-want-${i.split(" ")[0]}`}
            onClick={() => toggle(i)}
            className={`tap-safe border px-2.5 py-2 text-xs transition-colors duration-150 ${
              want.includes(i)
                ? "border-[#0B5C2E] bg-[#0B5C2E] text-white"
                : "border-border bg-white text-slate-600 hover:border-[#0B5C2E]"}`}>
            {i}
          </button>
        ))}
      </div>
      <Button className="mt-3" size="sm" data-testid={`${testid}-send`}
        disabled={busy || email.trim().length < 5} onClick={send}>
        Keep me posted
      </Button>
    </section>
  );
};
